import React, { useState } from "react";
import { Button } from '../../components/Button';
import { Badge } from '../../components/Badge';
import { formatDate } from '../../utils/dateFormatter';

interface DonationDetailsSideNavProps {
    isOpen: boolean;
    onClose: () => void;
    donationDetails: {
        donorName: string;
        donorEmail: string;
        donationValue: number;
        donationCurrency: string;
        donationID: string;
        donationType: string;
        appType: string;
        donationFrequency: string;
        campaign: string | { name: string };
        localPartner: string;
        shipment: string;
        assignedStatus: string;
        items: Array<{ itemName: string; unitCost: number; quantity: number }>;
        frequancey: string;
        createAt: string;
    } | null;
}

const DonationDetailsSideNav: React.FC<DonationDetailsSideNavProps> = ({
    isOpen,
    onClose,
    donationDetails,
}) => {
    const [activeTab, setActiveTab] = useState<'details' | 'history'>('details');

    const donationTypeLabels: Record<string, string> = {
        MANUAL_ADDED: 'Manual added',
        BULK_UPLOAD: 'Bulk upload',
        OWNER_REWARD_REDEMPTION: 'Owner reward redeemed',
        KIOSK_WEB_CHECKOUT: 'Kiosk web checkout',
        TAP_TO_PAY_TERMINAL: 'Tap to pay terminal',
    };

    if (!isOpen || !donationDetails) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-end bg-black bg-opacity-30">
            <div className="bg-white dark:bg-slate-900 rounded-lg shadow-lg w-full max-w-2xl h-full overflow-y-auto">
                {/* Sticky Header */}
                <div className="sticky top-0 bg-white dark:bg-slate-900 border-b dark:border-white/10 z-10">
                    {/* Header Content */}
                    <div className="flex items-center px-6 py-4">
                        <Button
                            variant="tertiary"
                            size="sm"
                            onClick={onClose}
                            aria-label="Close"
                            className="mr-4"
                        >
                            &times;
                        </Button>
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex-1">Donation details</h2>
                        <Badge
                            label="Unassigned"
                            variant="warning"
                            size="md"
                        />
                    </div>

                    {/* Tabs */}
                    <div className="border-b dark:border-white/10">
                        <div className="flex">
                            <button
                                className={`flex-1 py-2 text-center ${
                                    activeTab === 'details' ? 'border-b-2 border-primary-100 font-bold dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'
                                }`}
                                onClick={() => setActiveTab('details')}
                            >
                                Details
                            </button>
                            <button
                                className={`flex-1 py-2 text-center ${
                                    activeTab === 'history' ? 'border-b-2 border-primary-100 font-bold dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'
                                }`}
                                onClick={() => setActiveTab('history')}
                            >
                                History
                            </button>
                        </div>
                    </div>
                </div>

                {/* Rest of the content */}
                <div className="px-6 py-4">
                    {activeTab === 'details' && (
                        <div>
                            {/* Details Tab Content */}
                            {/* Donor Details */}
                            <div className="mb-4">
                                <div className="text-gray-500 dark:text-gray-400 mb-1">Donor</div>
                                <div className="flex items-center">
                                    <div className="w-10 h-10 bg-primary-70 text-white rounded-full flex items-center justify-center font-bold mr-4">
                                        {donationDetails.donorName ? donationDetails.donorName.charAt(0) : '?'}
                                    </div>
                                    <div>
                                        <div className="font-semibold text-lg text-gray-900 dark:text-gray-100">{donationDetails.donorName || 'Unknown Donor'}</div>
                                        <div className="text-sm text-gray-600 dark:text-gray-400">{donationDetails.donorEmail || 'No email provided'}</div>
                                    </div>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                                <div>
                                    <div className="text-gray-500 dark:text-gray-400 mb-1">Donation value</div>
                                    <div className="font-semibold text-lg text-gray-900 dark:text-gray-100">${donationDetails.donationValue}</div>
                                </div>
                                {/* Payment Status */}
                                <div className="mb-4">
                                    <div className="text-gray-500 dark:text-gray-400 mb-1">Payment status</div>
                                    <Badge
                                        label="Pending"
                                        variant="warning"
                                        size="md"
                                    />
                                </div>
                                <div>
                                    <div className="text-gray-500 dark:text-gray-400 mb-1">Donation Currency</div>
                                    <div className="font-semibold text-lg text-gray-900 dark:text-gray-100">{donationDetails.donationCurrency}</div>
                                </div>
                                <div>
                                    <div className="text-gray-500 dark:text-gray-400 mb-1">Currency value</div>
                                    <div className="font-semibold text-lg text-gray-900 dark:text-gray-100">{donationDetails.donationValue} {donationDetails.donationCurrency}</div>
                                </div>
                                <div>
                                    <div className="text-gray-500 dark:text-gray-400 mb-1">Donation ID</div>
                                    <div className="font-semibold text-lg text-gray-900 dark:text-gray-100">{donationDetails.donationID}</div>
                                </div>
                                <div>
                                    <div className="text-gray-500 dark:text-gray-400 mb-1">Donation type</div>
                                    <div className="font-semibold text-lg text-gray-900 dark:text-gray-100">
                                        {donationTypeLabels[donationDetails.donationType] || donationDetails.donationType}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-gray-500 dark:text-gray-400 mb-1">Source</div>
                                    <div className="font-semibold text-lg text-gray-900 dark:text-gray-100">
                                        {donationTypeLabels[donationDetails.appType] || donationDetails.appType || 'N/A'}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-gray-500 dark:text-gray-400 mb-1">Donation frequency</div>
                                    <div className="font-semibold text-lg text-gray-900 dark:text-gray-100">{donationDetails.frequancey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</div>
                                </div>
                                 <div>
                                    <div className="text-gray-500 dark:text-gray-400 mb-1"></div>
                                    <div className="font-semibold text-lg text-gray-900 dark:text-gray-100"></div>
                                </div>
                                <div>
                                    <div className="text-gray-500 dark:text-gray-400 mb-1">Campaign</div>
                                    <div className="font-semibold text-lg text-gray-900 dark:text-gray-100">
                                        {typeof donationDetails.campaign === 'string'
                                            ? donationDetails.campaign
                                            : donationDetails.campaign?.name || "N/A"}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-gray-500 dark:text-gray-400 mb-1">Local partner</div>
                                    <div className="font-semibold text-lg text-gray-900 dark:text-gray-100">{donationDetails.localPartner}</div>
                                </div>
                                <div>
                                    <div className="text-gray-500 dark:text-gray-400 mb-1">Shipment</div>
                                    <div className="font-semibold text-lg text-gray-900 dark:text-gray-100">{donationDetails.shipment}</div>
                                </div>
                            </div>

                            {/* Items */}
                            <div className="border-t dark:border-white/10 pt-4">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Items</h3>
                                <table className="min-w-full text-sm">
                                    <thead>
                                        <tr className="bg-gray-50 dark:bg-slate-800">
                                            <th className="px-4 py-2 text-left font-medium">Item</th>
                                            <th className="px-4 py-2 text-left font-medium">Unit cost</th>
                                            <th className="px-4 py-2 text-left font-medium">Quantity</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {donationDetails?.items?.map((item, idx) => (
                                            <tr key={idx} className="border-t dark:border-white/10 hover:bg-gray-50 dark:hover:bg-slate-800">
                                                <td className="px-4 py-2">{item.itemName}</td>
                                                <td className="px-4 py-2">{item.unitCost}</td>
                                                <td className="px-4 py-2">{item.quantity}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                    {activeTab === 'history' && donationDetails && (
                        <div className="text-sm">
                          <div className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Created</div>
                          <div className="mb-2">
                            Name: <span className="whitespace-pre">"{donationDetails.donorName}"</span><br />
                            Project Name: <span className="whitespace-pre">"{typeof donationDetails.campaign === 'string' ? donationDetails.campaign : donationDetails.campaign?.name}"</span><br />
                            Donation Value: <span className="whitespace-pre">"${donationDetails.donationValue.toFixed(2)}"</span><br />
                            Unit Cost: <span className="whitespace-pre">"${donationDetails.items?.[0]?.unitCost?.toFixed(2) ?? '0.00'}"</span>
                          </div>
                          <div className="mb-2">
                            {donationDetails.createAt
                              ? formatDate(donationDetails.createAt)
                              : ''}
                          </div>
                          <div>{donationDetails.donorName}</div>
                          <div className="text-gray-600 dark:text-gray-400">{donationDetails.donorEmail}</div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DonationDetailsSideNav;
